import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, Wallet, AlertCircle, Smile, Check, X, MessageCircle, ArrowRight, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import RemindersDialog from "@/components/RemindersDialog";
import { KpiCard, PageHeader, Empty } from "@/components/KpiCard";
import { StatusBadge, ChargeBadge, ReturnBadge } from "@/components/StatusBadge";
import { AppointmentDetailDialog } from "@/components/AppointmentDetailDialog";
import { WhatsAppDialog } from "@/components/WhatsAppDialog";
import { useApi, useResource, errMsg, notifyRefresh } from "@/lib/api";
import { STATUS, STATUS_KEYS, brl, fmtDate, fmtLong } from "@/lib/constants";
import { useSettings } from "@/lib/settings";

const TodayList = ({ items, onOpen, onWa }) => {
  const api = useApi();
  const quick = async (a, status) => {
    try { await api.patch(`/appointments/${a.id}/status`, { status }); toast.success("Status atualizado"); notifyRefresh(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!items.length) return <Empty testId="today-empty">Nenhuma consulta hoje</Empty>;
  return (
    <div className="space-y-2" data-testid="today-appointments">
      {items.map((a) => (
        <div key={a.id} className="group grid min-w-0 grid-cols-[4px_minmax(0,1fr)] gap-x-3 gap-y-2 rounded-xl border border-slate-100 bg-white px-3 py-3 transition-colors hover:border-sky-200 sm:flex sm:items-center" data-testid={`today-appt-${a.id}`}>
          <span className={`row-span-3 h-full min-h-10 w-1 rounded-full sm:row-span-1 sm:h-10 ${STATUS[a.effective_status].dot}`} />
          <button className="min-w-0 flex-1 text-left" onClick={() => onOpen(a)}>
            <p className="font-mono-num text-xs font-semibold text-slate-400">{a.time} · {a.duration} min</p>
            <p className="truncate font-semibold text-slate-800">{a.patient_name}</p>
            <p className="truncate text-xs text-slate-500">{a.procedure}</p>
          </button>
          <div className="col-start-2 min-w-0 justify-self-start sm:shrink-0"><StatusBadge status={a.effective_status} /></div>
          <div className="col-start-2 flex flex-wrap items-center gap-1 sm:shrink-0">
            <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-600 hover:bg-emerald-50" title="Concluído" onClick={() => quick(a, "concluido")} data-testid={`today-done-${a.id}`}><Check className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600 hover:bg-rose-50" title="Faltou" onClick={() => quick(a, "faltou")} data-testid={`today-noshow-${a.id}`}><X className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700 hover:bg-emerald-50" title="WhatsApp" onClick={() => onWa({ name: a.patient_name, phone: a.patient_phone, template: "consulta", vars: { data: fmtDate(a.date), hora: a.time } })} data-testid={`today-wa-${a.id}`}><MessageCircle className="h-4 w-4" /></Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const SideList = ({ title, link, children, testId }) => (
  <div className="surface rise min-w-0 p-3 sm:p-5" data-testid={testId}>
    <div className="mb-3 flex min-w-0 flex-wrap items-start justify-between gap-2">
      <h3 className="min-w-0 flex-1 basis-full break-words font-display font-bold leading-snug text-slate-900 sm:basis-auto">{title}</h3>
      <Link to={link} className="inline-flex shrink-0 items-center whitespace-nowrap text-xs font-semibold text-sky-700 hover:underline">Ver todos <ArrowRight className="ml-1 h-3 w-3" /></Link>
    </div>
    <div className="min-w-0 space-y-2">{children}</div>
  </div>
);

export default function Dashboard() {
  const { data } = useResource("/dashboard");
  const { data: allAppointments } = useResource("/appointments");
  const { data: reminders } = useResource("/reminders");
  const s = useSettings();
  const [sel, setSel] = useState(null);
  const [wa, setWa] = useState(null);
  const [remindersOpen, setRemindersOpen] = useState(false);
  if (!data) return <div className="h-40 animate-pulse rounded-2xl bg-sky-50" />;
  const c = data.status_counts;
  const currentTime = new Date().toLocaleString("sv-SE", { timeZone: "America/Sao_Paulo" }).replace(" ", "T").slice(0, 16);
  const upcoming = (Array.isArray(allAppointments) ? allAppointments : (allAppointments?.items || []))
    .filter((a) => a.status === "agendado" && `${a.date}T${a.time}` >= currentTime)
    .sort((a, b) => `${a.date}T${a.time}`.localeCompare(`${b.date}T${b.time}`))
    .slice(0, 15);
  const pendingReminders = (Array.isArray(reminders) ? reminders : []).filter((r) => !r.completed).length;
  const first = (s.dentist_name || "").split(" ").slice(0, 2).join(" ");

  return (
    <div className="min-w-0 w-full max-w-full">
      <PageHeader title={first ? `Olá, ${first}` : "Painel do consultório"} subtitle={fmtLong(data.today)} />
      <button type="button" onClick={() => setRemindersOpen(true)} data-testid="dashboard-reminders-btn"
        className="mb-5 flex w-full min-w-0 flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 px-5 py-4 text-left text-white shadow-lg shadow-orange-200/50 transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 sm:px-6">
        <span className="flex min-w-0 items-center gap-3">
          <span className="shrink-0 rounded-xl bg-white/20 p-2.5"><BellRing className="h-6 w-6" aria-hidden="true" /></span>
          <span className="min-w-0"><span className="block text-lg font-extrabold tracking-wide">LEMBRETES</span>
            <span className="block text-xs font-medium text-white/90">Pacientes, tarefas e anotações da clínica</span></span>
        </span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-bold">{pendingReminders} pendentes</span>
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </span>
      </button>
      <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Consultas hoje" value={data.appointments.length} hint={`${c.concluido} concluídas · ${c.agendado + c.atrasado} pendentes`} icon={CalendarCheck} testId="kpi-today" />
        <KpiCard label="Recebido no mês" value={brl(data.received_month)} icon={Wallet} tone="emerald" testId="kpi-received" delay={60} />
        <KpiCard label="A receber" value={brl(data.receivable)} hint={`${brl(data.overdue)} em atraso`} icon={AlertCircle} tone="amber" testId="kpi-receivable" delay={120} />
        <KpiCard label="Aparelho pendente" value={data.ortho_pending.length} hint={`${data.returns_due.length} retornos a agendar`} icon={Smile} tone="cyan" testId="kpi-ortho" delay={180} />
      </div>
      <div className="mt-6 grid min-w-0 grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
        <div className="surface rise min-w-0 p-3 sm:p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-lg font-bold text-slate-900">Agenda de hoje</h2>
            <div className="flex flex-wrap gap-3 text-xs text-slate-500">
              {STATUS_KEYS.map((k) => <span key={k} className="flex items-center gap-1"><span className={`h-2 w-2 rounded-full ${STATUS[k].dot}`} />{STATUS[k].label} {c[k]}</span>)}
            </div>
          </div>
          <TodayList items={data.appointments} onOpen={setSel} onWa={setWa} />
        </div>
        <div className="min-w-0 space-y-5">
          <SideList title="Mensalidades de aparelho em aberto" link="/mensalidades" testId="dash-ortho-list">
            {data.ortho_pending.length === 0 && <Empty>Todas pagas neste mês</Empty>}
            {data.ortho_pending.map((m) => (
              <div key={m.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-50/70 px-3 py-2 sm:flex-nowrap">
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <p className="break-words text-sm font-semibold text-slate-800">{m.patient_name}</p>
                  <p className="text-xs text-slate-500">{brl(m.remaining)} · vence {fmtDate(m.due_date)}</p>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-1 sm:shrink-0">
                  <ChargeBadge status={m.status} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700" onClick={() => setWa({ name: m.patient_name, phone: m.patient_phone, template: "mensalidade", vars: { valor: brl(m.remaining), vencimento: fmtDate(m.due_date) } })} data-testid={`dash-ortho-wa-${m.id}`}><MessageCircle className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </SideList>
          <SideList title="Retornos a agendar" link="/mensalidades?tab=retornos" testId="dash-returns-list">
            {data.returns_due.length === 0 && <Empty>Nenhum retorno pendente</Empty>}
            {data.returns_due.map((r) => (
              <div key={r.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-50/70 px-3 py-2 sm:flex-nowrap">
                <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                  <Link to={`/pacientes/${r.id}`} className="break-words text-sm font-semibold text-slate-800 hover:text-sky-700">{r.name}</Link>
                  <p className="text-xs text-slate-500">Previsto {fmtDate(r.return_plan.next_date)}</p>
                </div>
                <div className="flex min-w-0 flex-wrap items-center gap-1 sm:shrink-0">
                  <ReturnBadge status={r.status} />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-emerald-700" onClick={() => setWa({ name: r.name, phone: r.phone, template: "retorno", vars: {} })} data-testid={`dash-return-wa-${r.id}`}><MessageCircle className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </SideList>
        </div>
      </div>
      <RemindersDialog open={remindersOpen} onOpenChange={setRemindersOpen} upcoming={upcoming} onAppointment={setSel} />
      <AppointmentDetailDialog appointment={sel} open={!!sel} onOpenChange={(v) => !v && setSel(null)} />
      <WhatsAppDialog open={!!wa} onOpenChange={(v) => !v && setWa(null)} name={wa?.name} phone={wa?.phone} template={wa?.template} vars={wa?.vars || {}} />
    </div>
  );
}
